import { debounce } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { GetOrdersApi } from "../../../../service/repos/order_repo";
import AutoCompleteField from "../../../new_components/common/autocomplete";

interface OrderSearchFieldProps {
  value: string;
  onSelect: (order_id: string) => void;
  id?: string;
  disabled?: boolean;
  required?: boolean;
}

// Typeahead order picker used by Host complaints (non-HPCL) to attach an
// order_id — the ERP create/edit forms never had a way to set this manually;
// it was only ever auto-derived from a device's order (see
// backfillOrderIdFromDevice, kazam-backend). This lets a user fill it in when
// that auto-resolution can't find one.
const OrderSearchField: React.FC<OrderSearchFieldProps> = ({
  value,
  onSelect,
  id,
  disabled = false,
  required = false,
}) => {
  const [search, setSearch] = useState(value || "");
  const [edit, setEdit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState<any[]>([]);

  useEffect(() => {
    setSearch(value || "");
  }, [value]);

  const searchOrders = async (query: string) => {
    setLoading(true);
    const res = await GetOrdersApi({ search: query, page: 1, count: 20 });
    setOrders(res.success ? (res.data.orders ?? []) : []);
    setLoading(false);
  };

  const debouncedSearch = useCallback(
    debounce((query: string) => searchOrders(query), 500),
    [],
  );

  useEffect(() => {
    if (search && search !== value) debouncedSearch(search);
  }, [search]);

  return (
    <div className="flex gap-1 items-center w-full">
      {edit ? (
        <AutoCompleteField
          id={id}
          loading={loading}
          value={search}
          required={required}
          placeholder="Search order id / customer mobile"
          onChange={(str) => setSearch(str)}
          onSelect={(val: any) => {
            setEdit(false);
            setSearch(val.value.order_id);
            onSelect(val.value.order_id);
          }}
          suggestions={orders.map((o) => ({
            label: (
              <div className="flex flex-col text-xs">
                <b>{o.order_id}</b>
                <span className="text-gray-500">
                  {[o.customer?.firstName, o.customer?.lastName]
                    .filter(Boolean)
                    .join(" ")}
                  {o.customer?.mobile ? ` · ${o.customer.mobile}` : ""}
                </span>
              </div>
            ),
            value: o,
          }))}
        />
      ) : (
        <div
          id={id}
          className={`text-sm px-3 py-2 h-8 rounded focus:outline-none w-full border ${
            disabled ? "" : "cursor-pointer"
          }`}
          onClick={() => {
            if (!disabled) setEdit(true);
          }}
        >
          {value || "Search order id"}
        </div>
      )}
    </div>
  );
};

export default OrderSearchField;
